import React, { useState, useEffect } from "react";
import livebid from "../../assets/livbid.png"; // Your icon/image
// import { derhumOrangeSign  , derhumWhiteSign } from "../../utils/data"; // Your custom orange sign (฿ or so)
import CurrencyDisplay from "../common/CurrencyDisplay";
import { format } from "../../utils/functions";
import { checkUserRegistered } from "../../services/userService.ts";
export default function CurrentBidBox({
  currentBid: propCurrentBid,
  onBid,
  onBidWithIncrement = () => { },
  isConnected = true,
  auctionLaneId,
  vehicleId,
  isPlacingBid = false,
  winningBid = null,
  startingBid = 0,
  bidIncrement = 10,
  isRedPulsing = false,
  isGreenPulsing = false,
  onResetManualBid = null,
  auctionStatus = "ongoing",
  showBidErrorMessage = false,
  bidErrorMessage = null,
  dataToDisplay = null,
  setShowBidErrorMessage = () => { },
  timeRemaining = 34,
}) {
  const [currentBid, setCurrentBid] = useState(parseInt(propCurrentBid));
  const [customBid, setCustomBid] = useState(parseInt(propCurrentBid));
  const [manualIncrement, setManualIncrement] = useState(0);

  const jumpBidButtons = [500, 1000, 2000, 5000, 10000, 15000, 20000];

  // Sync from parent when prop changes (but not when we locally update)
  useEffect(() => {
    setCurrentBid(parseInt(propCurrentBid));
    setCustomBid(parseInt(propCurrentBid));
  }, [propCurrentBid]);

  // Reset manual increment to next winning bid amount
  const resetManualIncrement = () => {
    if (winningBid) {
      const nextAmount = parseInt(winningBid.amount) + parseInt(bidIncrement);
      setManualIncrement(nextAmount);
    } else {
      setManualIncrement(parseInt(startingBid) + parseInt(bidIncrement) || 0);
    }
  };

  // Handle manual increment/decrement
  const handleIncrementManualBid = () => {
    setManualIncrement((prev) => prev + parseInt(bidIncrement));
  };

  const handleDecrementManualBid = () => {
    const minAmount = winningBid?.amount || startingBid || 0;
    setManualIncrement((prev) =>
      Math.max(prev - parseInt(bidIncrement), minAmount),
    );
  };

  const handleManualBidInputChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setManualIncrement(value);
  };

  // Reset manual increment when winning bid changes
  useEffect(() => {
    resetManualIncrement();
    setShowBidErrorMessage(false);
  }, [winningBid, startingBid, bidIncrement]);

  // Expose reset function to parent component
  useEffect(() => {
    if (onResetManualBid) {
      onResetManualBid(resetManualIncrement);
    }
  }, [onResetManualBid]);

  // Handle placing a bid
  const handlePlaceBid = (bidValue) => {
    const newBid = currentBid + bidValue;
    onBid(newBid);
  };

  // CSS for pulsing animations and pause overlay
  const pulsingStyles = `
@keyframes pulse-red {
  0% {
    background-color: #ffffff;
    border-color: #ffffff;
  }
  50% {
    background-color: #ffebee; /* Soft pink/red background */
    border-color: #f44336;     /* Vibrant red border */
  }
  100% {
    background-color: #ffffff;
    border-color: #ffffff;
  }
}

@keyframes pulse-red-solid {
  0% {
    background-color: #f44336; /* Solid red background */
    border-color: #f44336;
    color: #ffffff;
  }
  50% {
    background-color: #ffffff;
    border-color: #f44336;
    color: #f44336;            /* Red text */
  }
  100% {
    background-color: #f44336;
    border-color: #f44336;
    color: #ffffff;
  }
}

.pulse-red, .pulse-red-solid {
  /* Both use the same red glow */
  box-shadow: 0 0 5px rgba(244, 67, 54, 0.5); 
}

@keyframes pulse-green {
  0% {
    background-color: #ffffff;
    border-color: #ffffff;
  }
  50% {
    background-color: rgb(204, 240, 204); /* Light green background */
    border-color: rgb(204, 240, 204);     /* Replaced dark green with light green to remove the dark effect */
  }
  100% {
    background-color: #ffffff;
    border-color: #ffffff;
  }
}


@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse-warning {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.pause-overlay {
  animation: fadeInUp 0.5s ease-out;
}

.pause-overlay .fa-pause-circle {
  animation: pulse-warning 2s ease-in-out infinite;
}
`;

  // Inject CSS styles for pulsing animations
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = pulsingStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [nextWinningBidAmount, setNextWinningBidAmount] = useState(currentBid);
  useEffect(() => {
    // calculate next winning bid amount
    if (winningBid) {
      const nextWinningBidAmount =
        parseInt(currentBid) + parseInt(bidIncrement);
      setNextWinningBidAmount(nextWinningBidAmount);
    }
  }, [winningBid, currentBid]);

  const [winningBidRegistered, setWinningBidRegistered] = useState(false);

  // query registration status for the high bidder whenever it updates
  useEffect(() => {
    if (winningBid?.bidder?.id) {
      checkUserRegistered(winningBid.bidder.id)
        .then((isReg) => setWinningBidRegistered(isReg))
        .catch(() => setWinningBidRegistered(false));
    } else {
      setWinningBidRegistered(false);
    }
  }, [winningBid]);

  return (
    <div className="flex flex-col select-none">
      
      {/* NEW: Full-width Status Boxes at the Top */}
      {isGreenPulsing && (
        <div className="w-full py-3 mb-4 rounded-lg text-center shadow-lg bg-green-400 border border-green-400">
          <span className="text-white font-extrabold text-xl tracking-widest uppercase drop-shadow-md">
            Approved
          </span>
        </div>
      )}

      {isRedPulsing && (
        <div className="w-full py-3 mb-4 rounded-lg text-center shadow-lg pulse-red-solid bg-red-400 font-extrabold text-xl tracking-widest uppercase">
          Under Approval
        </div>
      )}

      {/* Time Remaining - Red Text - Medium Size */}
      <div className="text-center mb-3">
        <div className="text-primary text-xs uppercase tracking-wide mb-1">
          Time Remaining
        </div>
        <div className="text-red-500 text-4xl font-bold leading-none">
          {timeRemaining} <span className="text-2xl">sec</span>
        </div>
      </div>

      {/* Current Bid Section - Medium Size */}
      <div className="bg-[#374151] rounded-lg p-4 mb-3">
        <div className="text-center">
          <div className="flex justify-between items-center text-gray-400 text-xs uppercase tracking-wide mb-1 px-1">
            <span className="  font-bold">Current Bid</span>
            {winningBid && (
              <span>
                High Bidder:{" "}
                <span className="text-white font-semibold">
                 
                  {winningBid.bidder?.id && (
                    <span >
                      {winningBidRegistered ? " Online User" : " Onsite User"}
                    </span>
                  )}
                </span>
              </span>
            )}
          </div>
          <div className="text-white text-4xl font-bold my-2">
           <span className=" text-4xl">$</span> {currentBid.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Bid Increment Buttons - Medium Size */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <button
          type="button"
          className="bg-[#334155] hover:bg-[#475569] text-white py-2.5 rounded-lg font-bold text-base transition disabled:opacity-50 disabled:cursor-not-allowed border border-gray-600 shadow-sm flex-1"
          onClick={() => onBidWithIncrement(500)}
          disabled={
            !isConnected ||
            isPlacingBid ||
            auctionStatus === "scheduled" ||
            auctionStatus === "paused"
          }
        >
          <span className="flex items-center justify-center gap-1">
            <span>+</span>
            <span className="text-sm">$</span>
            <span>500</span>
          </span>
        </button>
        <button
          type="button"
          className="bg-[#334155] hover:bg-[#475569] text-white py-2.5 rounded-lg font-bold text-base transition disabled:opacity-50 disabled:cursor-not-allowed border border-gray-600 shadow-sm flex-1"
          onClick={() => onBidWithIncrement(1000)}
          disabled={
            !isConnected ||
            isPlacingBid ||
            auctionStatus === "scheduled" ||
            auctionStatus === "paused"
          }
        >
          <span className="flex items-center justify-center gap-1">
            <span>+</span>
            <span className="text-sm">$</span>
            <span>1000</span>
          </span>
        </button>
      </div>

      {/* Manual Bid Input - Medium Size */}
      <div className="flex items-center px-5 gap-3 mb-3">
        <button
          type="button"
          className="w-8 h-8 rounded-lg bg-[#3b82f6] hover:bg-[#2563eb] text-white font-bold text-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-sm"
          onClick={handleDecrementManualBid}
          disabled={
            !isConnected ||
            isPlacingBid ||
            auctionStatus === "scheduled" ||
            auctionStatus === "paused" ||
            manualIncrement <= (winningBid?.amount || startingBid || 0)
          }
          title="Decrease bid amount"
        >
          −
        </button>
        <div className="flex-1  bg-[#0f172a] border border-gray-700 rounded-lg h-12 flex items-center justify-center px-4 text-center shadow-inner">
          <div className="flex  items-center gap-2 text-white font-bold tracking-wider text-lg sm:text-xl md:text-2xl">
            <span className="whitespace-nowrap">$</span>
            <span className="whitespace-nowrap">
              {manualIncrement.toLocaleString()}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="w-8 h-8 rounded-lg bg-[#3b82f6] hover:bg-[#2563eb] text-white font-bold text-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-sm"
          onClick={handleIncrementManualBid}
          disabled={
            !isConnected ||
            isPlacingBid ||
            auctionStatus === "scheduled" ||
            auctionStatus === "paused"
          }
          title="Increase bid amount"
        >
          +
        </button>
      </div>

      {/* Place Bid Button - Medium Size */}
      <button
        type="button"
        className="w-full py-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-blue-500/20"
        onClick={() => onBid(manualIncrement)}
        disabled={
          !isConnected ||
          isPlacingBid ||
          auctionStatus === "scheduled" ||
          auctionStatus === "paused" ||
          manualIncrement <= (winningBid?.amount || startingBid || 0)
        }
      >
        <span className="tracking-wide">PLACE BID</span>
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M14 5l7 7m0 0l-7 7m7-7H3"
          />
        </svg>
        <span className="inline-flex items-center gap-1 bg-blue-700/50 px-2 py-0.5 rounded text-blue-50 text-sm whitespace-nowrap">
          <span>$</span>
          <span>{manualIncrement.toLocaleString()}</span>
        </span>
      </button>

      {/* Success Message */}
      {winningBid?.bidder?.id ===
        JSON.parse(localStorage.getItem("authUser") || "{}")?.id && (
          <div className="mt-5 text-center">
            <span className="text-green-500 font-bold text-sm tracking-widest uppercase glow-text">
              You are the Highest Bidder
            </span>
          </div>
        )}

      {showBidErrorMessage && (
        <div className="mt-4 bg-red-200 text-red-600 text-sm text-center p-2 rounded-md">
          {bidErrorMessage}
        </div>
      )}

      {/* Bid Jump Section
      <div className="border-t border-gray-200 pt-5 flex flex-col items-center gap-5">
        <div className="text-xs text-center md:text-[16px] font-semibold text-[rgba(19,19,19,1)] tracking-wider">
          Bid Jump
        </div>
        <div className="flex flex-col w-full gap-2">
          {jumpBidButtons.map((jumpBidValue) => (
            <button
              type="button"
              className="bg-[#181728] py-3 text-xs font-bold text-white hover:bg-orange-500 transition-colors duration-200 disabled:opacity-50"
              disabled={
                !isConnected ||
                isPlacingBid ||
                auctionStatus === "scheduled" ||
                auctionStatus === "paused"
              }
              onClick={() => onBidWithIncrement(jumpBidValue)}
            >
              Bid +
              <CurrencyDisplay
                amount={jumpBidValue}
                size="sm"
                className="text-white text-xs"
                signOptions={{
                  width: "15",
                  height: "15",
                }}
              />
            </button>
          ))}
        </div>
      </div> */}
    </div>
  );
}
